/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { isEqual } from "lodash";

import { OperationStatus } from "Constants/OperationStatus";
import { defaultListingPageSize, defaultMvpFilterValues } from "Constants/ProfileConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { removeDateTimeOffset } from "Utility/FormatHelper";

import { IMVPState } from "./IMVPState";
import {
  IMVPAddNewCommentFail,
  IMVPClaimBenefitFail,
  IMVPExtendAzureBenefitCreditFailure,
  IMVPFetchAwardKitsBenefitDetailFailure,
  IMVPFetchAwardKitsBenefitDetailSuccess,
  IMVPFetchCommentsFail,
  IMVPFetchCommentsSuccess,
  IMVPFetchM365BenefitDetailFailure,
  IMVPFetchM365BenefitDetailSuccess,
  IMVPFetchProfileBenefitsFail,
  IMVPFetchProfileBenefitsSuccess,
  IMVPFetchProgramProfileFail,
  IMVPFetchProgramProfileSuccess,
  IMVPFetchRetireProfileFail,
  IMVPFetchRetireProfileSuccess,
  IMVPFetchTeamsBenefitDetailFailure,
  IMVPFetchTeamsBenefitDetailSuccess,
  IMVPFetchUserProfileFail,
  IMVPFetchUserProfileSuccess,
  IMVPRetireProfileFail,
  IMVPUpdateM365BenefitDetailFailure,
  IMVPUpdateM365BenefitDetailSuccess,
  IMVPUpdateProgramProfileFail,
  IMVPUserProfilesSearch,
  IMVPUserProfilesSearchFail,
  IMVPUserProfilesSearchSuccess,
  IMVPUserProfileAnnualExceptionUpdateFail,
  IMVPUserProfileIsPrivateUpdateFail,
  IMVPUserProfilePrimaryEmailUpdateFail,
  IMVPUserProfileSearchCurrentPageUpdate,
  IMVPUserProfileSearchFilterValuesBulkRemove,
  IMVPUserProfileSearchFilterValueAdd,
  IMVPUserProfileSearchFilterValueRemove,
  IMVPUserProfileSearchFilterValueUpdate,
  IMVPUserProfileSearchPageSizeUpdate,
  IMVPUserProfileSearchSortingChange,
  IMVPUserProfileUpdateBlockShippingFail,
  IMVPFetchMS365BenefitDetailFailure,
  IMVPFetchMS365BenefitDetailSuccess,
  IMVPUpdateMS365BenefitDetailFailure,
  IMVPUpdateMS365BenefitDetailSuccess,
  MVPActionTypes,
  IMVPAddAzureBenefitDetailFail,
} from "./MVPAction";
import {
  MVP_ADD_COMMENT,
  MVP_ADD_COMMENT_FAIL,
  MVP_ADD_COMMENT_SUCCESS,
  MVP_CLAIM_BENEFIT_MANAGER,
  MVP_CLAIM_BENEFIT,
  MVP_CLAIM_BENEFIT_FAIL,
  MVP_CLAIM_BENEFIT_RESET,
  MVP_CLAIM_BENEFIT_SUCCESS,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT_RESET,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS,
  MVP_FETCH_COMMENTS,
  MVP_FETCH_COMMENTS_FAIL,
  MVP_FETCH_COMMENTS_SUCCESS,
  MVP_FETCH_PROFILE_BENEFITS,
  MVP_FETCH_PROFILE_BENEFITS_FAIL,
  MVP_FETCH_PROFILE_BENEFITS_SUCCESS,
  MVP_FETCH_PROGRAM_PROFILE,
  MVP_FETCH_PROGRAM_PROFILE_FAIL,
  MVP_FETCH_PROGRAM_PROFILE_SUCCESS,
  MVP_FETCH_RETIRE_PROFILE,
  MVP_FETCH_RETIRE_PROFILE_FAIL,
  MVP_FETCH_RETIRE_PROFILE_SUCCESS,
  MVP_FETCH_USER_PROFILE,
  MVP_FETCH_USER_PROFILE_FAIL,
  MVP_FETCH_USER_PROFILE_SUCCESS,
  MVP_GET_AWARD_KITS_BENEFIT_DETAIL,
  MVP_GET_AWARD_KITS_BENEFIT_DETAIL_FAILURE,
  MVP_GET_AWARD_KITS_BENEFIT_DETAIL_SUCCESS,
  MVP_GET_M365_BENEFIT_DETAIL,
  MVP_GET_M365_BENEFIT_DETAIL_FAILURE,
  MVP_GET_M365_BENEFIT_DETAIL_SUCCESS,
  MVP_GET_TEAMS_BENEFIT_DETAIL,
  MVP_GET_TEAMS_BENEFIT_DETAIL_FAILURE,
  MVP_GET_TEAMS_BENEFIT_DETAIL_SUCCESS,
  MVP_RESET_M365_BENEFIT_DETAIL,
  MVP_RETIRE_PROFILE,
  MVP_RETIRE_PROFILE_FAIL,
  MVP_RETIRE_PROFILE_SUCCESS,
  MVP_UPDATE_M365_BENEFIT_DETAIL,
  MVP_UPDATE_M365_BENEFIT_DETAIL_FAILURE,
  MVP_UPDATE_M365_BENEFIT_DETAIL_SUCCESS,
  MVP_UPDATE_PROGRAM_PROFILE,
  MVP_UPDATE_PROGRAM_PROFILE_FAIL,
  MVP_UPDATE_PROGRAM_PROFILE_RESET,
  MVP_UPDATE_PROGRAM_PROFILE_SUCCESS,
  MVP_USER_PROFILE_FILTER_VALUE_RESET,
  MVP_USER_PROFILE_SEARCH_CURRENT_PAGE_UPDATE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_ADD,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_REMOVE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_STATE_PROVINCE_REMOVE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUE_UPDATE,
  MVP_USER_PROFILE_SEARCH_FILTER_VALUES_BULK_REMOVE,
  MVP_USER_PROFILE_SEARCH_PAGE_SIZE_UPDATE,
  MVP_USER_PROFILE_SEARCH_SORTING_UPDATE,
  MVP_USER_PROFILES_SEARCH,
  MVP_USER_PROFILES_SEARCH_FAIL,
  MVP_USER_PROFILES_SEARCH_SUCCESS,
  MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION,
  MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_FAIL,
  MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_SUCCESS,
  MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING,
  MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL,
  MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS,
  MVP_USERPROFILE_UPDATE_IS_PRIVATE,
  MVP_USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
  MVP_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_FAIL,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_RESET,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS,
  MVP_ADD_AZURE_BENEFIT_DETAIL,
  MVP_ADD_AZURE_BENEFIT_DETAIL_SUCCESS,
  MVP_ADD_AZURE_BENEFIT_DETAIL_FAIL,
  MVP_ADD_AZURE_BENEFIT_RESET,
  MVP_RESET_MS365_BENEFIT_DETAIL,
  MVP_GET_MS365_BENEFIT_DETAIL,
  MVP_GET_MS365_BENEFIT_DETAIL_FAILURE,
  MVP_GET_MS365_BENEFIT_DETAIL_SUCCESS,
  MVP_UPDATE_MS365_BENEFIT_DETAIL,
  MVP_UPDATE_MS365_BENEFIT_DETAIL_FAILURE,
  MVP_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS,
  MVP_RETIRE_PROFILE_RESET,
} from "./MVPActionTypes";

const INITIAL_STATE: IMVPState = {
  azureBenefitExtendCreditState: {
    extendAzureCreditOperationStatus: { update: OperationStatus.Idle },
    extendAzureCreditError: "",
  },
  azureBenefitAddState: {
    addAzureBenefitOperationStatus: { update: OperationStatus.Idle },
    addAzureBenefitError: "",
  },
  m365BenefitDetailState: {
    m365FetchErrorMessage: "",
    m365UpdateErrorMessage: "",
    m365OperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
    m365BenefitDetail: {
      tenantId: "",
      tenantName: "",
      userProfileId: 0,
    },
  },
  ms365BenefitDetailState: {
    ms365FetchErrorMessage: "",
    ms365UpdateErrorMessage: "",
    ms365OperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
    ms365BenefitDetail: {
      subscriptionId: "",
      tenantId: "",
      tenantName: "",
      userProfileId: 0,
    },
  },
  teamsBenefitDetailState: {
    teamsFetchErrorMessage: "",
    teamsUpdateErrorMessage: "",
    teamsOperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
    teamsBenefitDetail: {
      userProfileId: 0,
      expirationDate: "",
      claimDate: "",
      code: "",
    },
  },
  awardKitsBenefitDetailState: {
    awardKitsFetchErrorMessage: "",
    awardKitsUpdateErrorMessage: "",
    awardKitsOperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
    awardKitsBenefitDetail: {
      userProfileId: 0,
      expirationDate: "",
      claimDate: "",
      code: "",
    },
  },
  benefitsState: {
    benefitsOperationStatus: { read: OperationStatus.Idle, create: OperationStatus.Idle },
    profileBenefits: {
      azure: [],
      githubPro: [],
      mcp: [],
      linkedIn: [],
      visualStudio: [],
      m365: [],
      benefitTenant: {},
      teams: [],
      awardKits: [],
    },
  },
  commentsState: {
    commentsOperationStatus: { read: OperationStatus.Idle, create: OperationStatus.Idle },
    userProfileComments: [],
  },
  demographicsState: {
    demographicsData: {
      numberOfApplications: 0,
      averageApplicationScore: 0,
      genderData: [],
      statusData: [],
      declineReasonData: [],
      awardCategoryBarData: [],
      regionBarData: [],
    },
    capData: {
      capacityData: [],
    },
    demographicsFilterValues: [],
    isLoadingDemographics: false,
    isLoadingCapData: false,
    loadDemographicsError: "",
    loadCapDataError: "",
    tenant: TenantTypesEnum.MVP,
  },
  selectedUserProfileState: {
    userProfileOperationStatus: {
      read: OperationStatus.Idle,
    },
    updateBlockShippingOperationStatus: OperationStatus.Idle,
    updateBlockShippingErrorMessage: "",
    updateIsPrivateProfileStatus: OperationStatus.Idle,
    updateIsPrivateProfileError: "",
    updatePrimaryEmailStatus: OperationStatus.Idle,
    updatePrimaryEmailErrorMessage: "",
    updateAnnualExceptionProfileStatus: OperationStatus.Idle,
    updateAnnualExceptionProfileError: "",
  },
  userProfilesSearchState: {
    currentPage: 1,
    filteredCount: 0,
    filteredUserProfiles: [],
    searchText: "",
    searchOperationStatus: { read: OperationStatus.Idle },
    pageSize: defaultListingPageSize,
    searchFilterValues: defaultMvpFilterValues,
  },
  programProfileState: {
    programProfileOperationStatus: { update: OperationStatus.Idle },
  },
  retireProfileState: {
    operationStatus: { update: OperationStatus.Idle },
  },
};

const applyFetchBenefits = (state: IMVPState): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { read: OperationStatus.Pending },
    profileBenefitsErrorMessage: undefined,
  },
});

const applyFetchProfileBenefitsSuccess = (
  state: IMVPState,
  action: IMVPFetchProfileBenefitsSuccess
): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { read: OperationStatus.Succeeded },
    profileBenefitsErrorMessage: undefined,
    profileBenefits: action.userProfileBenefits,
  },
});

const applyFetchBenefitsFail = (
  state: IMVPState,
  action: IMVPFetchProfileBenefitsFail
): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { read: OperationStatus.Failed },
    profileBenefitsErrorMessage: action.profileBenefitsErrorMessage,
  },
});

/**
 * Benefit manager claiming operation.
 * applyClaimingBenefitSuccess and applyClaimingBenefitFail actions will be used.
 * @param state - The current state of the MVP reducer.
 * @returns
 */
const applyClaimingBenefitManager = (state: IMVPState): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { create: OperationStatus.Pending },
    claimBenefitError: undefined,
  },
});

const applyClaimingBenefit = (state: IMVPState): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { create: OperationStatus.Pending },
    claimBenefitError: undefined,
  },
});

const applyClaimingBenefitSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { create: OperationStatus.Succeeded },
  },
});

const applyClaimingBenefitFail = (state: IMVPState, action: IMVPClaimBenefitFail): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { create: OperationStatus.Failed },
    claimBenefitError: action.error,
  },
});

const applyClaimingBenefitReset = (state: IMVPState): IMVPState => ({
  ...state,
  benefitsState: {
    ...state.benefitsState,
    benefitsOperationStatus: { create: OperationStatus.Idle },
    claimBenefitError: "",
  },
});

const applyExtendAzureBenefitCredit = (state: IMVPState): IMVPState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Pending },
    extendAzureCreditError: "",
  },
});

const applyExtendAzureBenefitCreditSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Succeeded },
  },
});

const applyExtendAzureBenefitCreditFail = (
  state: IMVPState,
  action: IMVPExtendAzureBenefitCreditFailure
): IMVPState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Failed },
    extendAzureCreditError: action.error,
  },
});

const applyExtendAzureBenefitCreditReset = (state: IMVPState): IMVPState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Idle },
    extendAzureCreditError: "",
  },
});

const applyFetchComments = (state: IMVPState): IMVPState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsErrorMessage: undefined,
    commentsOperationStatus: { read: OperationStatus.Pending },
  },
});

const applyFetchCommentsSuccess = (
  state: IMVPState,
  action: IMVPFetchCommentsSuccess
): IMVPState => ({
  ...state,
  commentsState: {
    commentsErrorMessage: undefined,
    commentsOperationStatus: { read: OperationStatus.Succeeded },
    userProfileComments: action.userProfileComments,
  },
});

const applyFetchCommentsFail = (state: IMVPState, action: IMVPFetchCommentsFail): IMVPState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsOperationStatus: { read: OperationStatus.Failed },
    commentsErrorMessage: action.errorMessage,
  },
});

const applyAddNewComment = (state: IMVPState): IMVPState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsErrorMessage: undefined,
    commentsOperationStatus: { create: OperationStatus.Pending },
  },
});

const applyAddNewCommentSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsErrorMessage: undefined,
    commentsOperationStatus: { create: OperationStatus.Succeeded },
  },
});

const applyAddNewCommentFail = (state: IMVPState, action: IMVPAddNewCommentFail): IMVPState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsOperationStatus: { create: OperationStatus.Failed },
    commentsErrorMessage: action.errorMessage,
  },
});

const applyFetchProgramProfile = (state: IMVPState): IMVPState => ({
  ...state,
  programProfileState: {
    programProfileOperationStatus: { read: OperationStatus.Pending },
    programProfileErrorMessage: undefined,
    programProfile: undefined,
  },
});

const applyFetchRetireProfile = (state: IMVPState): IMVPState => ({
  ...state,
  retireProfileState: {
    operationStatus: { read: OperationStatus.Pending },
    errorMessage: undefined,
    retireProfile: undefined,
  },
});

const applyFetchProgramProfileSuccess = (
  state: IMVPState,
  action: IMVPFetchProgramProfileSuccess
): IMVPState => ({
  ...state,
  programProfileState: {
    ...state.programProfileState,
    programProfileOperationStatus: { read: OperationStatus.Succeeded },
    programProfile: action.programProfile,
    programProfileErrorMessage: undefined,
  },
});

const applyFetchRetireProfileSuccess = (
  state: IMVPState,
  action: IMVPFetchRetireProfileSuccess
): IMVPState => ({
  ...state,
  retireProfileState: {
    ...state.retireProfileState,
    operationStatus: { read: OperationStatus.Succeeded },
    retireProfile: action.retireProfile,
    errorMessage: undefined,
  },
});

const applyFetchProgramProfileFail = (
  state: IMVPState,
  action: IMVPFetchProgramProfileFail
): IMVPState => ({
  ...state,
  programProfileState: {
    ...state.programProfileState,
    programProfileOperationStatus: { read: OperationStatus.Failed },
    programProfile: undefined,
    programProfileErrorMessage: action.errorMessage,
  },
});

const applyAddAzureBenefit = (state: IMVPState): IMVPState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Pending },
    addAzureBenefitError: "",
  },
});

const applyAddAzureBenefitSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Succeeded },
  },
});

const applyAddAzureBenefitFail = (
  state: IMVPState,
  action: IMVPAddAzureBenefitDetailFail
): IMVPState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Failed },
    addAzureBenefitError: action.errorMessage,
  },
});

const applyAddAzureBenefitReset = (state: IMVPState): IMVPState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Idle },
    addAzureBenefitError: "",
  },
});

const applyFetchRetireProfileFail = (
  state: IMVPState,
  action: IMVPFetchRetireProfileFail
): IMVPState => ({
  ...state,
  retireProfileState: {
    ...state.retireProfileState,
    operationStatus: { read: OperationStatus.Failed },
    retireProfile: undefined,
    errorMessage: action.errorMessage,
  },
});

const applyUpdateProgramProfile = (state: IMVPState): IMVPState => ({
  ...state,
  programProfileState: {
    ...state.programProfileState,
    programProfileOperationStatus: { update: OperationStatus.Pending },
    programProfileErrorMessage: undefined,
  },
});

const applyRetireProfileReset = (state: IMVPState): IMVPState => ({
  ...state,
  retireProfileState: {
    ...state.retireProfileState,
    operationStatus: { update: OperationStatus.Idle },
    errorMessage: "",
  },
});

const applyRetireProfile = (state: IMVPState): IMVPState => ({
  ...state,
  retireProfileState: {
    ...state.retireProfileState,
    operationStatus: { update: OperationStatus.Pending },
    errorMessage: undefined,
  },
});

const applyUpdateProgramProfileSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  programProfileState: {
    ...state.programProfileState,
    programProfileOperationStatus: { update: OperationStatus.Succeeded },
    programProfileErrorMessage: undefined,
  },
});

const applyRetireProfileSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  retireProfileState: {
    ...state.retireProfileState,
    operationStatus: { update: OperationStatus.Succeeded },
    errorMessage: undefined,
  },
});

const applyUpdateProgramProfileFail = (
  state: IMVPState,
  action: IMVPUpdateProgramProfileFail
): IMVPState => ({
  ...state,
  programProfileState: {
    ...state.programProfileState,
    programProfileOperationStatus: { update: OperationStatus.Failed },
    programProfileErrorMessage: action.errorMessage,
  },
});

const applyRetireProfileFail = (state: IMVPState, action: IMVPRetireProfileFail): IMVPState => ({
  ...state,
  retireProfileState: {
    ...state.retireProfileState,
    operationStatus: { update: OperationStatus.Failed },
    errorMessage: action.errorMessage,
  },
});

const applyUpdateProgramProfileReset = (state: IMVPState): IMVPState => ({
  ...state,
  programProfileState: {
    ...state.programProfileState,
    programProfileOperationStatus: { update: OperationStatus.Idle },
    programProfileErrorMessage: "",
  },
});

const applyFetchUserProfile = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    userProfileOperationStatus: { read: OperationStatus.Pending },
    fetchUserProfileErrorMessage: undefined,
    selectedUserProfile: undefined,
    updateBlockShippingOperationStatus: OperationStatus.Idle,
    updateBlockShippingErrorMessage: "",
    updateIsPrivateProfileError: "",
    updateIsPrivateProfileStatus: OperationStatus.Idle,
    updateAnnualExceptionProfileError: "",
    updateAnnualExceptionProfileStatus: OperationStatus.Idle,
  },
});

const applyFetchUserProfileSuccess = (
  state: IMVPState,
  action: IMVPFetchUserProfileSuccess
): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    userProfileOperationStatus: { read: OperationStatus.Succeeded },
    selectedUserProfile: {
      ...action.userProfile,
      dateOfBirth: removeDateTimeOffset(action.userProfile.dateOfBirth),
    },
    fetchUserProfileErrorMessage: undefined,
    updateBlockShippingOperationStatus: OperationStatus.Idle,
    updateBlockShippingErrorMessage: "",
    updateIsPrivateProfileError: "",
    updateIsPrivateProfileStatus: OperationStatus.Idle,
    updateAnnualExceptionProfileError: "",
    updateAnnualExceptionProfileStatus: OperationStatus.Idle,
  },
});

const applyFetchUserProfileFail = (
  state: IMVPState,
  action: IMVPFetchUserProfileFail
): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    userProfileOperationStatus: { read: OperationStatus.Failed },
    selectedUserProfile: undefined,
    fetchUserProfileErrorMessage: action.errorMessage,
    updateBlockShippingOperationStatus: OperationStatus.Idle,
    updateBlockShippingErrorMessage: "",
    updateIsPrivateProfileError: "",
    updateIsPrivateProfileStatus: OperationStatus.Idle,
    updateAnnualExceptionProfileError: "",
    updateAnnualExceptionProfileStatus: OperationStatus.Idle,
  },
});

const applyUserProfilesSearch = (state: IMVPState, action: IMVPUserProfilesSearch): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchText: action.searchText,
    currentPage: action.searchPage,
    pageSize: action.pageSize,
    searchFilterValues: action.selectedFilterValues,
    searchOperationStatus: { read: OperationStatus.Pending },
  },
});

const applyUserProfilesSearchSuccess = (
  state: IMVPState,
  action: IMVPUserProfilesSearchSuccess
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchOperationStatus: { read: OperationStatus.Succeeded },
    filteredUserProfiles: action.userProfiles,
    currentPage: action.searchPage,
    filteredCount: action.filteredCount,
    pageSize: action.pageSize,
  },
});

const applyUserProfilesSearchFail = (
  state: IMVPState,
  action: IMVPUserProfilesSearchFail
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchOperationStatus: { read: OperationStatus.Failed },
    searchErrorMessage: action.errorMessage,
  },
});

const applyUserProfilesSearchFilterValueAdd = (
  state: IMVPState,
  action: IMVPUserProfileSearchFilterValueAdd
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchFilterValues: [...state.userProfilesSearchState.searchFilterValues, action.filterValue],
  },
});

const applyUserProfilesSearchFilterValueRemove = (
  state: IMVPState,
  action: IMVPUserProfileSearchFilterValueRemove
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchFilterValues: state.userProfilesSearchState.searchFilterValues.filter(
      (value) => !isEqual(value, action.filterValue)
    ),
  },
});

const applyUserProfilesSearchFilterValuesBulkRemove = (
  state: IMVPState,
  action: IMVPUserProfileSearchFilterValuesBulkRemove
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchFilterValues: state.userProfilesSearchState.searchFilterValues.filter(
      (sfv) => !action.filterValues.some((fv) => fv.value === sfv.value)
    ),
  },
});

const applyUserProfilesSearchFilterValueStateProvinceRemove = (state: IMVPState): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchFilterValues: state.userProfilesSearchState.searchFilterValues.filter(
      (value) => !isEqual(value.fieldName, "stateOrProvince")
    ),
  },
});

const applyUserProfilesSearchFilterValueUpdate = (
  state: IMVPState,
  action: IMVPUserProfileSearchFilterValueUpdate
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    searchFilterValues: [
      ...state.userProfilesSearchState.searchFilterValues.filter(
        (value) => !isEqual(value, action.filterValue)
      ),
      action.filterValue,
    ],
  },
});

const applyUserProfilesSearchPageSizeUpdate = (
  state: IMVPState,
  action: IMVPUserProfileSearchPageSizeUpdate
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    currentPage: 1, // Resetting the page to 1 when the page size is changed
    pageSize: action.pageSize,
  },
});

const applyUserProfilesSearchCurrentPageUpdate = (
  state: IMVPState,
  action: IMVPUserProfileSearchCurrentPageUpdate
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    currentPage: action.currentPage,
  },
});

const applyM365BenefitDetailReset = (state: IMVPState): IMVPState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365OperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
  },
});

const applyGetM365BenefitDetailsSuccess = (
  state: IMVPState,
  action: IMVPFetchM365BenefitDetailSuccess
): IMVPState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365BenefitDetail: action.m365BenefitDetail,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      read: OperationStatus.Succeeded,
    },
  },
});

const applyGetM365BenefitDetailsFailure = (
  state: IMVPState,
  action: IMVPFetchM365BenefitDetailFailure
): IMVPState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365FetchErrorMessage: action.m365FetchErrorMessage,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      read: OperationStatus.Failed,
    },
  },
});

const applyGetM365BenefitDetails = (state: IMVPState): IMVPState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365FetchErrorMessage: "",
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      read: OperationStatus.Pending,
    },
  },
});

const applyUpdateM365BenefitDetails = (state: IMVPState): IMVPState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365UpdateErrorMessage: "",
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      update: OperationStatus.Pending,
    },
  },
});

const applyUpdateM365BenefitDetailsSuccess = (
  state: IMVPState,
  action: IMVPUpdateM365BenefitDetailSuccess
): IMVPState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365BenefitDetail: action.m365BenefitDetail,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      update: OperationStatus.Succeeded,
    },
  },
});

const applyUpdateM365BenefitDetailsFailure = (
  state: IMVPState,
  action: IMVPUpdateM365BenefitDetailFailure
): IMVPState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365FetchErrorMessage: action.m365UpdateErrorMessage,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      update: OperationStatus.Failed,
    },
  },
});

const applyMS365BenefitDetailReset = (state: IMVPState): IMVPState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365OperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
  },
});

const applyGetMS365BenefitDetailsSuccess = (
  state: IMVPState,
  action: IMVPFetchMS365BenefitDetailSuccess
): IMVPState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365BenefitDetail: action.ms365BenefitDetail,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      read: OperationStatus.Succeeded,
    },
  },
});

const applyGetMS365BenefitDetailsFailure = (
  state: IMVPState,
  action: IMVPFetchMS365BenefitDetailFailure
): IMVPState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365FetchErrorMessage: action.ms365FetchErrorMessage,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      read: OperationStatus.Failed,
    },
  },
});

const applyGetMS365BenefitDetails = (state: IMVPState): IMVPState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365FetchErrorMessage: "",
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      read: OperationStatus.Pending,
    },
  },
});

const applyUpdateMS365BenefitDetails = (state: IMVPState): IMVPState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365UpdateErrorMessage: "",
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      update: OperationStatus.Pending,
    },
  },
});

const applyUpdateMS365BenefitDetailsSuccess = (
  state: IMVPState,
  action: IMVPUpdateMS365BenefitDetailSuccess
): IMVPState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365BenefitDetail: action.ms365BenefitDetail,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      update: OperationStatus.Succeeded,
    },
  },
});

const applyUpdateMS365BenefitDetailsFailure = (
  state: IMVPState,
  action: IMVPUpdateMS365BenefitDetailFailure
): IMVPState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365FetchErrorMessage: action.ms365UpdateErrorMessage,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      update: OperationStatus.Failed,
    },
  },
});

const applyGetTeamsBenefitDetailsSuccess = (
  state: IMVPState,
  action: IMVPFetchTeamsBenefitDetailSuccess
): IMVPState => ({
  ...state,
  teamsBenefitDetailState: {
    ...state.teamsBenefitDetailState,
    teamsBenefitDetail: action.teamsBenefitDetail,
    teamsOperationStatus: {
      ...state.teamsBenefitDetailState.teamsOperationStatus,
      read: OperationStatus.Succeeded,
    },
  },
});

const applyGetTeamsBenefitDetailsFailure = (
  state: IMVPState,
  action: IMVPFetchTeamsBenefitDetailFailure
): IMVPState => ({
  ...state,
  teamsBenefitDetailState: {
    ...state.teamsBenefitDetailState,
    teamsFetchErrorMessage: action.teamsFetchErrorMessage,
    teamsOperationStatus: {
      ...state.teamsBenefitDetailState.teamsOperationStatus,
      read: OperationStatus.Failed,
    },
  },
});

const applyGetTeamsBenefitDetails = (state: IMVPState): IMVPState => ({
  ...state,
  teamsBenefitDetailState: {
    ...state.teamsBenefitDetailState,
    teamsFetchErrorMessage: "",
    teamsOperationStatus: {
      ...state.teamsBenefitDetailState.teamsOperationStatus,
      read: OperationStatus.Pending,
    },
  },
});

const applyGetAwardKitsBenefitDetailsSuccess = (
  state: IMVPState,
  action: IMVPFetchAwardKitsBenefitDetailSuccess
): IMVPState => ({
  ...state,
  awardKitsBenefitDetailState: {
    ...state.awardKitsBenefitDetailState,
    awardKitsBenefitDetail: action.awardKitsBenefitDetail,
    awardKitsOperationStatus: {
      ...state.awardKitsBenefitDetailState.awardKitsOperationStatus,
      read: OperationStatus.Succeeded,
    },
  },
});

const applyGetAwardKitsBenefitDetailsFailure = (
  state: IMVPState,
  action: IMVPFetchAwardKitsBenefitDetailFailure
): IMVPState => ({
  ...state,
  awardKitsBenefitDetailState: {
    ...state.awardKitsBenefitDetailState,
    awardKitsFetchErrorMessage: action.awardKitsFetchErrorMessage,
    awardKitsOperationStatus: {
      ...state.awardKitsBenefitDetailState.awardKitsOperationStatus,
      read: OperationStatus.Failed,
    },
  },
});

const applyGetAwardKitsBenefitDetails = (state: IMVPState): IMVPState => ({
  ...state,
  awardKitsBenefitDetailState: {
    ...state.awardKitsBenefitDetailState,
    awardKitsFetchErrorMessage: "",
    awardKitsOperationStatus: {
      ...state.awardKitsBenefitDetailState.awardKitsOperationStatus,
      read: OperationStatus.Pending,
    },
  },
});

const applyUpdateBlockShipping = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateBlockShippingOperationStatus: OperationStatus.Pending,
    updateBlockShippingErrorMessage: "",
  },
});

const applyUpdateBlockShippingFail = (
  state: IMVPState,
  action: IMVPUserProfileUpdateBlockShippingFail
): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateBlockShippingOperationStatus: OperationStatus.Failed,
    updateBlockShippingErrorMessage: action.errorMessage,
  },
});

const applyUpdateBlockShippingSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateBlockShippingOperationStatus: OperationStatus.Succeeded,
    updateBlockShippingErrorMessage: "",
  },
});

const applyUserProfileIsPrivateUpdate = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateIsPrivateProfileStatus: OperationStatus.Pending,
    updateIsPrivateProfileError: "",
  },
});

const applyUserProfileIsPrivateUpdateSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateIsPrivateProfileStatus: OperationStatus.Succeeded,
  },
});

const applyUserProfileIsPrivateUpdateFail = (
  state: IMVPState,
  action: IMVPUserProfileIsPrivateUpdateFail
): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateIsPrivateProfileStatus: OperationStatus.Failed,
    updateIsPrivateProfileError: action.error,
  },
});

const applyUserProfileAnnualExceptionUpdate = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateAnnualExceptionProfileStatus: OperationStatus.Pending,
    updateAnnualExceptionProfileError: "",
  },
});

const applyUserProfileAnnualExceptionUpdateSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateAnnualExceptionProfileStatus: OperationStatus.Succeeded,
  },
});

const applyUserProfileAnnualExceptionUpdateFail = (
  state: IMVPState,
  action: IMVPUserProfileAnnualExceptionUpdateFail
): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updateAnnualExceptionProfileStatus: OperationStatus.Failed,
    updateAnnualExceptionProfileError: action.error,
  },
});

const applyUserProfilesSearchSortingUpdate = (
  state: IMVPState,
  action: IMVPUserProfileSearchSortingChange
): IMVPState => ({
  ...state,
  userProfilesSearchState: {
    ...state.userProfilesSearchState,
    currentPage:
      state.userProfilesSearchState.currentPage > 1 ? 1 : state.userProfilesSearchState.currentPage,
    sortColumn: action.sortColumn,
    sortDescending: action.sortDescending,
  },
});

const applyUserProfileFilterValueReset = (state: IMVPState): IMVPState => ({
  ...state,
  userProfilesSearchState: INITIAL_STATE.userProfilesSearchState,
});

const applyUserProfilePrimaryEmailUpdate = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updatePrimaryEmailStatus: OperationStatus.Pending,
    updatePrimaryEmailErrorMessage: "",
  },
});

const applyUserProfilePrimaryEmailUpdateFail = (
  state: IMVPState,
  action: IMVPUserProfilePrimaryEmailUpdateFail
): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updatePrimaryEmailStatus: OperationStatus.Failed,
    updatePrimaryEmailErrorMessage: action.errorMessage,
  },
});

const applyUserProfilePrimaryEmailUpdateSuccess = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updatePrimaryEmailStatus: OperationStatus.Succeeded,
  },
});

const applyUserProfilePrimaryEmailUpdateReset = (state: IMVPState): IMVPState => ({
  ...state,
  selectedUserProfileState: {
    ...state.selectedUserProfileState,
    updatePrimaryEmailStatus: OperationStatus.Idle,
    updatePrimaryEmailErrorMessage: "",
  },
});

function mvpReducer(state = INITIAL_STATE, action: MVPActionTypes): IMVPState {
  switch (action.type) {
    case MVP_RESET_M365_BENEFIT_DETAIL:
      return applyM365BenefitDetailReset(state);
    case MVP_GET_M365_BENEFIT_DETAIL:
      return applyGetM365BenefitDetails(state);
    case MVP_GET_M365_BENEFIT_DETAIL_FAILURE:
      return applyGetM365BenefitDetailsFailure(state, action);
    case MVP_GET_M365_BENEFIT_DETAIL_SUCCESS:
      return applyGetM365BenefitDetailsSuccess(state, action);
    case MVP_UPDATE_M365_BENEFIT_DETAIL:
      return applyUpdateM365BenefitDetails(state);
    case MVP_UPDATE_M365_BENEFIT_DETAIL_SUCCESS:
      return applyUpdateM365BenefitDetailsSuccess(state, action);
    case MVP_UPDATE_M365_BENEFIT_DETAIL_FAILURE:
      return applyUpdateM365BenefitDetailsFailure(state, action);
    case MVP_RESET_MS365_BENEFIT_DETAIL:
      return applyMS365BenefitDetailReset(state);
    case MVP_GET_MS365_BENEFIT_DETAIL:
      return applyGetMS365BenefitDetails(state);
    case MVP_GET_MS365_BENEFIT_DETAIL_FAILURE:
      return applyGetMS365BenefitDetailsFailure(state, action);
    case MVP_GET_MS365_BENEFIT_DETAIL_SUCCESS:
      return applyGetMS365BenefitDetailsSuccess(state, action);
    case MVP_UPDATE_MS365_BENEFIT_DETAIL:
      return applyUpdateMS365BenefitDetails(state);
    case MVP_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS:
      return applyUpdateMS365BenefitDetailsSuccess(state, action);
    case MVP_UPDATE_MS365_BENEFIT_DETAIL_FAILURE:
      return applyUpdateMS365BenefitDetailsFailure(state, action);
    case MVP_GET_TEAMS_BENEFIT_DETAIL:
      return applyGetTeamsBenefitDetails(state);
    case MVP_GET_TEAMS_BENEFIT_DETAIL_FAILURE:
      return applyGetTeamsBenefitDetailsFailure(state, action);
    case MVP_GET_TEAMS_BENEFIT_DETAIL_SUCCESS:
      return applyGetTeamsBenefitDetailsSuccess(state, action);
    case MVP_GET_AWARD_KITS_BENEFIT_DETAIL:
      return applyGetAwardKitsBenefitDetails(state);
    case MVP_GET_AWARD_KITS_BENEFIT_DETAIL_FAILURE:
      return applyGetAwardKitsBenefitDetailsFailure(state, action);
    case MVP_GET_AWARD_KITS_BENEFIT_DETAIL_SUCCESS:
      return applyGetAwardKitsBenefitDetailsSuccess(state, action);
    case MVP_EXTEND_AZURE_BENEFIT_CREDIT:
      return applyExtendAzureBenefitCredit(state);
    case MVP_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS:
      return applyExtendAzureBenefitCreditSuccess(state);
    case MVP_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE:
      return applyExtendAzureBenefitCreditFail(state, action);
    case MVP_EXTEND_AZURE_BENEFIT_CREDIT_RESET:
      return applyExtendAzureBenefitCreditReset(state);
    case MVP_ADD_AZURE_BENEFIT_DETAIL:
      return applyAddAzureBenefit(state);
    case MVP_ADD_AZURE_BENEFIT_DETAIL_SUCCESS:
      return applyAddAzureBenefitSuccess(state);
    case MVP_ADD_AZURE_BENEFIT_DETAIL_FAIL:
      return applyAddAzureBenefitFail(state, action);
    case MVP_ADD_AZURE_BENEFIT_RESET:
      return applyAddAzureBenefitReset(state);
    case MVP_FETCH_PROFILE_BENEFITS:
      return applyFetchBenefits(state);
    case MVP_FETCH_PROFILE_BENEFITS_FAIL:
      return applyFetchBenefitsFail(state, action);
    case MVP_FETCH_PROFILE_BENEFITS_SUCCESS:
      return applyFetchProfileBenefitsSuccess(state, action);
    case MVP_CLAIM_BENEFIT_MANAGER:
      return applyClaimingBenefitManager(state);
    case MVP_CLAIM_BENEFIT:
      return applyClaimingBenefit(state);
    case MVP_CLAIM_BENEFIT_SUCCESS:
      return applyClaimingBenefitSuccess(state);
    case MVP_CLAIM_BENEFIT_FAIL:
      return applyClaimingBenefitFail(state, action);
    case MVP_CLAIM_BENEFIT_RESET:
      return applyClaimingBenefitReset(state);
    case MVP_FETCH_COMMENTS:
      return applyFetchComments(state);
    case MVP_FETCH_COMMENTS_SUCCESS:
      return applyFetchCommentsSuccess(state, action);
    case MVP_FETCH_COMMENTS_FAIL:
      return applyFetchCommentsFail(state, action);
    case MVP_ADD_COMMENT:
      return applyAddNewComment(state);
    case MVP_ADD_COMMENT_SUCCESS:
      return applyAddNewCommentSuccess(state);
    case MVP_ADD_COMMENT_FAIL:
      return applyAddNewCommentFail(state, action);
    case MVP_FETCH_PROGRAM_PROFILE:
      return applyFetchProgramProfile(state);
    case MVP_FETCH_PROGRAM_PROFILE_SUCCESS:
      return applyFetchProgramProfileSuccess(state, action);
    case MVP_FETCH_PROGRAM_PROFILE_FAIL:
      return applyFetchProgramProfileFail(state, action);
    case MVP_UPDATE_PROGRAM_PROFILE:
      return applyUpdateProgramProfile(state);
    case MVP_UPDATE_PROGRAM_PROFILE_SUCCESS:
      return applyUpdateProgramProfileSuccess(state);
    case MVP_UPDATE_PROGRAM_PROFILE_FAIL:
      return applyUpdateProgramProfileFail(state, action);
    case MVP_UPDATE_PROGRAM_PROFILE_RESET:
      return applyUpdateProgramProfileReset(state);
    case MVP_FETCH_USER_PROFILE:
      return applyFetchUserProfile(state);
    case MVP_FETCH_USER_PROFILE_SUCCESS:
      return applyFetchUserProfileSuccess(state, action);
    case MVP_FETCH_USER_PROFILE_FAIL:
      return applyFetchUserProfileFail(state, action);
    case MVP_USER_PROFILES_SEARCH:
      return applyUserProfilesSearch(state, action);
    case MVP_USER_PROFILES_SEARCH_SUCCESS:
      return applyUserProfilesSearchSuccess(state, action);
    case MVP_USER_PROFILES_SEARCH_FAIL:
      return applyUserProfilesSearchFail(state, action);
    case MVP_USER_PROFILE_SEARCH_FILTER_VALUE_ADD:
      return applyUserProfilesSearchFilterValueAdd(state, action);
    case MVP_USER_PROFILE_SEARCH_FILTER_VALUE_REMOVE:
      return applyUserProfilesSearchFilterValueRemove(state, action);
    case MVP_USER_PROFILE_SEARCH_FILTER_VALUES_BULK_REMOVE:
      return applyUserProfilesSearchFilterValuesBulkRemove(state, action);
    case MVP_USER_PROFILE_SEARCH_FILTER_VALUE_STATE_PROVINCE_REMOVE:
      return applyUserProfilesSearchFilterValueStateProvinceRemove(state);
    case MVP_USER_PROFILE_SEARCH_FILTER_VALUE_UPDATE:
      return applyUserProfilesSearchFilterValueUpdate(state, action);
    case MVP_USER_PROFILE_SEARCH_PAGE_SIZE_UPDATE:
      return applyUserProfilesSearchPageSizeUpdate(state, action);
    case MVP_USER_PROFILE_SEARCH_CURRENT_PAGE_UPDATE:
      return applyUserProfilesSearchCurrentPageUpdate(state, action);
    case MVP_USER_PROFILE_SEARCH_SORTING_UPDATE:
      return applyUserProfilesSearchSortingUpdate(state, action);
    case MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING:
      return applyUpdateBlockShipping(state);
    case MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL:
      return applyUpdateBlockShippingFail(state, action);
    case MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS:
      return applyUpdateBlockShippingSuccess(state);
    case MVP_USERPROFILE_UPDATE_IS_PRIVATE: {
      return applyUserProfileIsPrivateUpdate(state);
    }
    case MVP_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS: {
      return applyUserProfileIsPrivateUpdateSuccess(state);
    }
    case MVP_USERPROFILE_UPDATE_IS_PRIVATE_FAIL: {
      return applyUserProfileIsPrivateUpdateFail(state, action);
    }
    case MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION: {
      return applyUserProfileAnnualExceptionUpdate(state);
    }
    case MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_SUCCESS: {
      return applyUserProfileAnnualExceptionUpdateSuccess(state);
    }
    case MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION_FAIL: {
      return applyUserProfileAnnualExceptionUpdateFail(state, action);
    }
    case MVP_USER_PROFILE_FILTER_VALUE_RESET: {
      return applyUserProfileFilterValueReset(state);
    }
    case MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL: {
      return applyUserProfilePrimaryEmailUpdate(state);
    }
    case MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_FAIL: {
      return applyUserProfilePrimaryEmailUpdateFail(state, action);
    }
    case MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS: {
      return applyUserProfilePrimaryEmailUpdateSuccess(state);
    }
    case MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL_RESET: {
      return applyUserProfilePrimaryEmailUpdateReset(state);
    }
    case MVP_FETCH_RETIRE_PROFILE:
      return applyFetchRetireProfile(state);
    case MVP_FETCH_RETIRE_PROFILE_SUCCESS:
      return applyFetchRetireProfileSuccess(state, action);
    case MVP_FETCH_RETIRE_PROFILE_FAIL:
      return applyFetchRetireProfileFail(state, action);
    case MVP_RETIRE_PROFILE_RESET:
      return applyRetireProfileReset(state);
    case MVP_RETIRE_PROFILE:
      return applyRetireProfile(state);
    case MVP_RETIRE_PROFILE_SUCCESS:
      return applyRetireProfileSuccess(state);
    case MVP_RETIRE_PROFILE_FAIL:
      return applyRetireProfileFail(state, action);
    default:
      return state;
  }
}

export default mvpReducer;
